'use client';
import { useMemo } from 'react';
import { compositeScore } from '@/lib/composite-score';
import { compositeTier as sharedTier } from '@/lib/composite-tiers';
import { SignalDot } from '@/components/SignalDot';
import { useHoldingsData, type HoldingHistory, type HoldingPnl, type HoldingSelfPctile } from '@/lib/hooks/use-holdings-data';
import { useResource } from '@/lib/use-resource';
import { SignalCell, buildCellTitles, type SymbolSignals, type HoldingsSignalsResp, type SignalCellType } from './HoldingSignalCells';
import { SignalDetailPopover } from './SignalDetailPopover';
import { TargetPriceModal } from '@/components/stock-detail/TargetPriceModal';
import { useState } from 'react';

// Same visual format as the Portfolio Holdings table (HoldingsList.tsx) but
// composed for the Alpha Zone basket: no WT column (basket is equal-weighted),
// adds %Since Add and %Since Anchor at the right edge. Reuses the same
// useHoldingsData hook so the underlying /api/stocks, /api/holdings-pnl,
// /api/portfolio-snapshot, /api/self-percentiles fetches are deduped with
// the Portfolio page (useResource shared cache).

export interface AlphaPerStockRow {
  symbol: string;
  name: string | null;
  sector: string | null;
  enteredOn: string | null;
  daysIn: number | null;
  pctSinceAddition: number | null;
  pctReturn: number | null;            // %since anchor (varies with the picker)
  weight: number | null;               // conviction-weight portfolio share (0..1)
}

interface Props {
  perStock: AlphaPerStockRow[];
  anchorDate: string;
  /** Click handler for the symbol cell — used by the parent to open the
   *  per-stock price chart. */
  onSelectSymbol?: (symbol: string) => void;
  selectedSymbol?: string | null;
}

const INDEX_ETFS = new Set(['SPY', 'QQQ', 'IWM', 'DIA', 'VTI', 'VOO', 'IVV']);

function pctColor(p: number | null): string {
  if (p == null) return '#475569';
  if (p >= 90) return '#22c55e';
  if (p >= 70) return '#4ade80';
  if (p >= 30) return '#94a3b8';
  if (p >= 10) return '#fb923c';
  return '#f87171';
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function compTier(c: number | null): { label: string; color: string } {
  const t = sharedTier(c);
  return { label: t.label, color: t.textClass };
}

/** ISO YYYY-MM-DD → YY/MM/DD (8 chars vs 10). Saves space in the symbol cell
 *  where the date sits inline next to the ticker. */
function shortIso(iso: string | null): string {
  if (!iso || iso.length < 10) return '—';
  return `${iso.slice(2, 4)}/${iso.slice(5, 7)}/${iso.slice(8, 10)}`;
}

/** "Micron Technology, Inc." → "Micron". Compact identifier next to ticker
 *  so the symbol cell becomes self-narrating without giving the full name. */
function firstWord(name: string | null): string {
  if (!name) return '';
  return name.replace(/[,.;]/g, '').trim().split(/\s+/)[0] ?? '';
}

function renderPnl(v: number | null) {
  if (v == null) return <span className="text-[#475569]">—</span>;
  const color = v > 0 ? 'text-emerald-400' : v < 0 ? 'text-red-400' : 'text-[#94a3b8]';
  const sign = v >= 0 ? '+' : '';
  return <span className={`tabular-nums ${color}`}>{`${sign}${(v * 100).toFixed(1)}%`}</span>;
}

// renderPctileCell removed 2026-05-19: Q/Q%/V/V%/C% columns dropped from the alpha-zone
// table to match /portfolio HoldingsList. Composite history (1m/3m/6m/1y) replaced them.
function _renderPctileCell_unused(axis: 'q' | 'v' | 'c', selfPctile: HoldingSelfPctile) {
  const p = selfPctile[axis];
  const regime = (axis === 'q' ? selfPctile.qRegime : axis === 'v' ? selfPctile.vRegime : selfPctile.cRegime) ?? '';
  const drift = regime.startsWith('model regime shift');
  const titleAxis = axis === 'q' ? 'Q' : axis === 'v' ? 'V' : 'composite';
  const display = p == null ? (drift ? 'drift' : '—') : ordinal(Math.round(p));
  const tip = p != null
    ? `${titleAxis}_self: ${ordinal(Math.round(p))} percentile of this stock's own multi-year history. Higher = better-than-usual on this axis.`
    : drift
    ? `${titleAxis}_self unavailable: model regime shift. Today's value sits >4.5σ from the historical mean — historical archive used a different model.`
    : `${titleAxis}_self: no history`;
  return (
    <div
      className={`col-span-1 text-right tabular-nums text-[10px] font-semibold ${drift ? 'italic' : ''}`}
      style={{ color: drift ? '#94a3b8' : pctColor(p) }}
      title={tip}
    >
      {display}
    </div>
  );
}

interface MergedRow {
  symbol: string;
  name: string | null;
  sector: string | null;
  price: number | null;
  marketCap: number | null;
  composite: number | null;
  q: number | null;
  v: number | null;
  history: HoldingHistory;
  pnl: HoldingPnl;
  selfPctile: HoldingSelfPctile;
  enteredOn: string | null;
  daysIn: number | null;
  pctSinceAddition: number | null;
  pctSinceAnchor: number | null;
  weight: number | null;
}

const EMPTY_HIST: HoldingHistory = { w1: null, m1: null, m3: null, m6: null, y1: null };
const EMPTY_PNL: HoldingPnl = { dayPct: null, mtdPct: null, ytdPct: null, off52wHigh: null };
const EMPTY_PCTILE: HoldingSelfPctile = { q: null, v: null, c: null, qRegime: '', vRegime: '', cRegime: '' };

interface TPRowAZ {
  symbol: string; fiveYearTarget: number | null; threeYearTarget: number | null;
  asymmetry: number | null; upside: number | null; downside: number | null; currentPrice: number | null;
  consensusFairValue?: number | null; todayFairValue?: number | null; methodCoverage?: number | null;
  peerGroup?: string; marketCap?: number | null; fy1Eps?: number | null;
  targetMultiple?: object | null; evEbitdaMultiple?: number | null; evEbitdaFairValue?: number | null;
  fwdEbitdaRaw?: number | null; fwdNetDebtRaw?: number | null; dcfFairValue?: number | null;
  dcfBreakdown?: object | null; dcfDiscountRate?: number | null; costOfEquityBreakdown?: object | null;
  ownerEarningsPerShare?: number | null; pfcfMultiple?: number | null; fcfPerShare?: number | null;
  fcfRaw?: number | null; pfcfFairValue?: number | null;
}
interface TPPayloadAZ { stocks?: TPRowAZ[] }

export function AlphaZoneHoldingsTable({ perStock, anchorDate, onSelectSymbol, selectedSymbol }: Props) {
  // Per-signal focused popover (Steve 2026-05-22 — click a Cong/Insid/Call/Earn
  // cell to see just that data slice, not the full StockStoryDrawer).
  const [popover, setPopover] = useState<{ symbol: string; signalType: SignalCellType } | null>(null);
  const [modalSymbol, setModalSymbol] = useState<string | null>(null);
  const { scores, historyBySymbol, pnlBySymbol, selfPctileBySymbol } = useHoldingsData();
  const { data: tpDataAZ } = useResource<TPPayloadAZ>('/api/target-prices');

  // Signal columns (Cong / Insid / Call / Earn) — replaces the 1m/3m/6m/1y
  // composite-history block (Steve directive 2026-05-22).
  const { data: signalsData } = useResource<HoldingsSignalsResp>('/api/holdings-signals');
  const signalsBySymbol: Record<string, SymbolSignals> = Object.fromEntries(
    (signalsData?.signals ?? []).map((s) => [s.symbol, s])
  );
  const { data: fcvData } = useResource<{ bySymbol: Record<string, { dC: number; dQ: number }> }>('/api/forward-c-velocity');
  const fcvBySymbol = fcvData?.bySymbol ?? {};
  const tpBySymbolAZ = (() => { const m = new Map<string, TPRowAZ>(); for (const r of tpDataAZ?.stocks ?? []) m.set(r.symbol, r); return m; })();

  const rows: MergedRow[] = useMemo(() => {
    return perStock.map((p) => {
      const s = scores?.get(p.symbol);
      const composite = s ? compositeScore(s.q ?? null, s.v ?? null) : null;
      return {
        symbol: p.symbol,
        name: p.name,
        sector: p.sector ?? s?.sector ?? null,
        price: s?.price ?? null,
        marketCap: s?.marketCap ?? null,
        composite: INDEX_ETFS.has(p.symbol) ? 0 : composite,
        q: s?.q ?? null,
        v: s?.v ?? null,
        history: historyBySymbol.get(p.symbol) ?? EMPTY_HIST,
        pnl: pnlBySymbol.get(p.symbol) ?? EMPTY_PNL,
        selfPctile: INDEX_ETFS.has(p.symbol) ? EMPTY_PCTILE : (selfPctileBySymbol.get(p.symbol) ?? EMPTY_PCTILE),
        enteredOn: p.enteredOn,
        daysIn: p.daysIn,
        pctSinceAddition: p.pctSinceAddition,
        pctSinceAnchor: p.pctReturn,
        weight: p.weight,
      };
    });
  }, [perStock, scores, historyBySymbol, pnlBySymbol, selfPctileBySymbol]);

  // 23 column-units — order mirrors /portfolio HoldingsList (2026-05-19 Steve directive):
  //   Symbol·Name(2) · $B(2) · WT%(1) · Px(2) · %1D · MTD · YTD · 52wH · 5Y$ · 5Y% · Asym
  //   · C now · C 1m · C 3m · C 6m · C 1y · Tier · αDays · Since add · Since anchor
  // Q/Q%/V/V%/C% removed (same as holdings); WT% takes Wt's slot; no Sh/Entry/Hedge
  // because they're portfolio-only. Alpha-zone keeps 4 historical composite windows
  // (1m/3m/6m/1y) where holdings uses 3 (1w/1m/6m) — longer-horizon read.
  return (
    <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg overflow-hidden">
      <div className="grid grid-cols-[repeat(26,minmax(0,1fr))] gap-1 px-2 py-2 text-[8px] uppercase tracking-wider text-[#64748b] border-b border-[#1e293b]">
        <div className="col-span-2">Symbol · Name</div>
        <div className="col-span-1 text-center" title="Active alerts from the Action Monitor">●</div>
        <div className="col-span-2 text-right" title="Market cap in billions">$B</div>
        <div className="col-span-1 text-right text-amber-300/80" title="Target portfolio weight (conviction rule: top-10, k=5, max 15%)">WT%</div>
        <div className="col-span-2 text-right" title="Current price (live FMP quote on last refresh)">Px</div>
        <div className="col-span-1 text-right" title="Today's price move (current vs prior close)">%1D</div>
        <div className="col-span-1 text-right" title="Month-to-date price return">MTD</div>
        <div className="col-span-1 text-right" title="Year-to-date price return">YTD</div>
        <div className="col-span-1 text-right" title="Percent below 52-week high">52wH</div>
        <div className="col-span-1 text-right text-amber-300/80" title="3-year target price = FY-3 EPS × target multiple. Anchored to real BBG 3-year analyst consensus EPS.">3Y $</div>
        <div className="col-span-1 text-right text-amber-300/80" title="3-year target % vs current price">3Y %</div>
        <div className="col-span-1 text-right text-blue-300/80" title="Consensus fair value (40% P/E + 30% EV/EBITDA + 20% DCF + 10% P/FCF). Click to see full derivation.">FV ↗</div>
        <div className="col-span-1 text-right text-amber-300/80" title="Asymmetry = |upside| / |downside|. Green ≥4:1 (Steve's bar), amber 2-4, red <2.">Asym</div>
        <div className="col-span-1 text-right text-amber-300" title="Composite score now (Q & V combined; α=1.25, β=0.90, shift=0.5)">C now</div>
        <div className="col-span-1 text-right text-violet-300/80" title="Simulated ΔC when next quarter reports at BBG consensus. TTM roll: removes oldest quarter, adds epsNextQ. Negative = blowout prior earnings raised the base, compressing forward CAGR.">nextQC</div>
        <div className="col-span-1 text-center text-[#cbd5e1]" title="Congressional buy vs sell (last 90d). +ve / -ve / = / — . Click for full disclosures.">Cong</div>
        <div className="col-span-1 text-center text-[#cbd5e1]" title="SEC Form 4 insider net-buying (last 6mo aggregate). Click for detail.">Insid</div>
        <div className="col-span-1 text-center text-[#cbd5e1]" title="Latest earnings-call tone from FMP transcript + Claude summary.">Call</div>
        <div className="col-span-1 text-center text-[#cbd5e1]" title="Earnings momentum: epsRevision z-score blended with recent earningsSurprise.">Earn</div>
        <div className="col-span-1 text-right">Tier</div>
        <div className="col-span-1 text-right text-emerald-300/80" title="Days in Alpha Zone since entry">αDays</div>
        <div className="col-span-1 text-right text-emerald-300/80" title="Return since the row's Alpha Zone entry date">Since add</div>
        <div className="col-span-1 text-right text-amber-300/80" title={`Return since the basket anchor date (${anchorDate})`}>Since anchor</div>
      </div>
      {rows.map((h) => {
        const tier = compTier(h.composite);
        const selected = selectedSymbol === h.symbol;
        return (
          <div
            key={h.symbol}
            onClick={() => onSelectSymbol?.(h.symbol)}
            className={`grid grid-cols-[repeat(26,minmax(0,1fr))] gap-1 px-2 text-[10px] leading-tight items-center border-b border-[#1e293b]/60 cursor-pointer transition-colors h-[24px] ${selected ? 'bg-amber-400/5' : 'hover:bg-[#1e293b]/30'}`}
          >
            <div className="col-span-2 whitespace-nowrap">
              <span className={`font-mono font-bold ${selected ? 'text-amber-300' : tier.color}`}>{h.symbol}</span>
              <span className="text-[10px] text-[#cbd5e1] ml-1.5 font-normal" title={h.name ?? ''}>{firstWord(h.name)}</span>
              <span className="text-[8px] text-[#64748b] font-mono ml-2" title={h.enteredOn ?? ''}>{shortIso(h.enteredOn)}</span>
            </div>
            <div className="col-span-1 flex items-center justify-center">
              <SignalDot symbol={h.symbol} size={6} />
            </div>
            <div className="col-span-2 text-right tabular-nums text-[#94a3b8]" title={h.name ?? ''}>
              {h.marketCap != null && h.marketCap > 0 ? `$${Math.round(h.marketCap / 1e9)}B` : '—'}
            </div>
            <div
              className="col-span-1 text-right tabular-nums font-semibold"
              title={h.weight != null ? `Target portfolio weight ${(h.weight * 100).toFixed(2)}% (conviction k=5, capped 15%)` : 'No weight set'}
              style={h.weight != null
                ? { color: h.weight >= 0.14 ? '#fbbf24' : h.weight >= 0.08 ? '#fcd34d' : h.weight >= 0.04 ? '#cbd5e1' : '#94a3b8' }
                : { color: '#475569' }}
            >
              {h.weight != null ? `${(h.weight * 100).toFixed(1)}%` : '—'}
            </div>
            <div className="col-span-2 text-right tabular-nums text-[#cbd5e1]" title={h.price != null ? 'Live price (last refresh)' : 'No price'}>
              {h.price != null ? `$${h.price.toFixed(2)}` : '—'}
            </div>
            <div className="col-span-1 text-right">{renderPnl(h.pnl.dayPct)}</div>
            <div className="col-span-1 text-right">{renderPnl(h.pnl.mtdPct)}</div>
            <div className="col-span-1 text-right">{renderPnl(h.pnl.ytdPct)}</div>
            <div className="col-span-1 text-right">{renderPnl(h.pnl.off52wHigh)}</div>
            {(() => {
              const tp = tpBySymbolAZ.get(h.symbol);
              const target = tp?.threeYearTarget ?? null;
              if (target == null) return <div className="col-span-1 text-right tabular-nums text-[#475569]">—</div>;
              return (
                <div className="col-span-1 text-right tabular-nums text-[#cbd5e1]" title={`3-year target = FY-3 EPS × target multiple = $${target.toFixed(2)}`}>
                  ${Math.round(target)}
                </div>
              );
            })()}
            {(() => {
              const tp = tpBySymbolAZ.get(h.symbol);
              const target = tp?.threeYearTarget ?? null;
              if (target == null || h.price == null || h.price <= 0) {
                return <div className="col-span-1 text-right tabular-nums text-[#475569]">—</div>;
              }
              const pct = (target / h.price - 1) * 100;
              const color = pct >= 50 ? 'text-emerald-400' : pct >= 0 ? 'text-amber-300' : 'text-red-400';
              return (
                <div className={`col-span-1 text-right tabular-nums ${color}`} title={`3Y target $${target.toFixed(2)} · asymmetry ${tp?.asymmetry?.toFixed(1) ?? "—"}:1`}>
                  {pct >= 0 ? '+' : ''}{pct.toFixed(0)}%
                </div>
              );
            })()}
            {(() => {
              const tp = tpBySymbolAZ.get(h.symbol);
              const fv = tp?.consensusFairValue ?? tp?.todayFairValue ?? null;
              if (fv == null || h.price == null || h.price <= 0) {
                return <div className="col-span-1 text-right tabular-nums text-[#475569]">—</div>;
              }
              const pct = (fv / h.price - 1) * 100;
              const color = pct >= 20 ? 'text-emerald-400' : pct >= 0 ? 'text-blue-300' : 'text-red-400';
              return (
                <div
                  className={`col-span-1 text-right tabular-nums ${color} cursor-pointer hover:text-blue-200 transition-colors`}
                  title={`Consensus FV $${fv.toFixed(2)} (${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%). Click to see derivation.`}
                  onClick={(e) => { e.stopPropagation(); setModalSymbol(h.symbol); }}
                >
                  ${Math.round(fv)}
                </div>
              );
            })()}
            {(() => {
              const tp = tpBySymbolAZ.get(h.symbol);
              const asym = tp?.asymmetry ?? null;
              if (asym == null) return <div className="col-span-1 text-right tabular-nums text-[#475569]">—</div>;
              const color = asym >= 4 ? 'text-emerald-400' : asym >= 2 ? 'text-amber-300' : 'text-red-400';
              const upPct = tp?.upside != null ? `${tp.upside >= 0 ? '+' : ''}${(tp.upside * 100).toFixed(0)}%` : '—';
              const downPct = tp?.downside != null ? `${(tp.downside * 100).toFixed(0)}%` : '—';
              return (
                <div className={`col-span-1 text-right tabular-nums ${color}`} title={`Up ${upPct} / Down ${downPct}`}>
                  {asym.toFixed(1)}:1
                </div>
              );
            })()}
            <div className={`col-span-1 text-right tabular-nums font-semibold ${tier.color}`}>
              {h.composite == null ? '—' : h.composite.toFixed(2)}
            </div>
            {(() => {
              const fcv = fcvBySymbol[h.symbol];
              if (!fcv) return <div className="col-span-1 text-right tabular-nums text-[#475569]">—</div>;
              const { dC } = fcv;
              const color = dC > 0.01 ? 'text-emerald-400' : dC < -0.01 ? 'text-red-400' : 'text-[#94a3b8]';
              const sign = dC >= 0 ? '+' : '';
              return (
                <div
                  className={`col-span-1 text-right tabular-nums font-semibold ${color}`}
                  title={`ΔC = ${sign}${dC.toFixed(3)} when next quarter reports at consensus. ΔQ = ${fcv.dQ >= 0 ? '+' : ''}${fcv.dQ.toFixed(3)}. Negative on strong earners = blowout base compresses forward CAGR.`}
                >
                  {sign}{dC.toFixed(2)}
                </div>
              );
            })()}
            {(() => {
              const s = signalsBySymbol[h.symbol];
              const titles = buildCellTitles(s);
              const click = (type: SignalCellType) => setPopover({ symbol: h.symbol, signalType: type });
              return (
                <>
                  <SignalCell signal={s?.cong    ?? 'none'} title={titles.cong}    onClick={() => click('cong')} />
                  <SignalCell signal={s?.insider ?? 'none'} title={titles.insider} onClick={() => click('insider')} />
                  <SignalCell signal={s?.call    ?? 'none'} title={titles.call}    onClick={() => click('call')} />
                  <SignalCell signal={s?.earn    ?? 'none'} title={titles.earn}    onClick={() => click('earn')} />
                </>
              );
            })()}
            <div className={`col-span-1 text-right text-[8px] uppercase tracking-wide whitespace-nowrap overflow-hidden ${tier.color}`} title={tier.label}>
              {tier.label}
            </div>
            <div className="col-span-1 text-right tabular-nums" title={h.daysIn != null ? `${h.daysIn}d in Alpha Zone` : '—'}>
              {h.daysIn != null ? <span className="text-emerald-400 font-semibold">{h.daysIn}d</span> : <span className="text-[#475569]">—</span>}
            </div>
            <div className="col-span-1 text-right">{renderPnl(h.pctSinceAddition)}</div>
            <div className="col-span-1 text-right">{renderPnl(h.pctSinceAnchor)}</div>
          </div>
        );
      })}
      {modalSymbol && (() => {
        const tp = tpBySymbolAZ.get(modalSymbol);
        if (!tp) return null;
        return (
          <TargetPriceModal
            d={{
              symbol: modalSymbol,
              currentPrice: tp.currentPrice,
              peerGroup: tp.peerGroup ?? 'standard',
              todayFairValue: tp.todayFairValue ?? null,
              fy1Eps: tp.fy1Eps ?? null,
              targetMultiple: tp.targetMultiple ?? null,
              evEbitdaMultiple: tp.evEbitdaMultiple ?? null,
              evEbitdaFairValue: tp.evEbitdaFairValue ?? null,
              fwdEbitdaRaw: tp.fwdEbitdaRaw ?? null,
              fwdNetDebtRaw: tp.fwdNetDebtRaw ?? null,
              marketCap: tp.marketCap ?? null,
              dcfFairValue: tp.dcfFairValue ?? null,
              dcfBreakdown: tp.dcfBreakdown ?? null,
              dcfDiscountRate: tp.dcfDiscountRate ?? null,
              costOfEquityBreakdown: tp.costOfEquityBreakdown ?? null,
              ownerEarningsPerShare: tp.ownerEarningsPerShare ?? null,
              pfcfMultiple: tp.pfcfMultiple ?? null,
              fcfPerShare: tp.fcfPerShare ?? null,
              fcfRaw: tp.fcfRaw ?? null,
              pfcfFairValue: tp.pfcfFairValue ?? null,
              consensusFairValue: tp.consensusFairValue ?? null,
              methodCoverage: tp.methodCoverage ?? null,
            }}
            onClose={() => setModalSymbol(null)}
          />
        );
      })()}
      {popover && (
        <SignalDetailPopover
          symbol={popover.symbol}
          signalType={popover.signalType}
          onClose={() => setPopover(null)}
        />
      )}
    </div>
  );
}
